import React, { useEffect, useReducer, useState } from 'react';
import { FaEye, FaSave } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../u2000Core/components/buttons/U2000ActionBtn';
import U2000CheckBox from '../../u2000Core/components/formControls/checkbox/U2000CheckBox';
import U2000InputCalendarCustom from '../../u2000Core/components/formControls/input/U2000InputCalendarCustom';
import U2000InputMask from '../../u2000Core/components/formControls/input/U2000InputMask';
import U2000InputText from '../../u2000Core/components/formControls/input/U2000InputText';
import U2000Label from '../../u2000Core/components/formControls/label/U2000Label';
import U2000Panel from '../../u2000Core/components/layout/U2000Panel';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import u2000FormState, {
  U2000FormStateEnumEditMode,
  U2000FormStateEnumState,
  type U2000FormState,
} from '../../u2000Core/reducer/formReducer/U2000FormState';
import {
  u2000IntFormState,
  useU2000FormStateHandlers,
} from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS, U2106ROLES } from '../U2106';
import { useU2106ProfileHandlers } from '../hooks/useU2106ProfileHandlers';
import {
  emptyU2106ProfileFormRow,
  u2106ProfileToFormRow,
  type U2106HourBanks,
  type U2106ProfileFormRow,
  type U2106ProfileResult,
} from '../U2106Service';
import { u2106CollectProfileFormErrors } from '../tools/U2106ProfileFormValidation';
import U2106MfaPanel from './U2106MfaPanel';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { doInitLU as doU3129InitLU } from '../../u3129Timesheet/U3129Service';
import { u2000SkipHourBankBalanceValidationOnSubmit } from '../../u2000Core/tools/u2000SkipHourBankBalanceValidationOnSubmit';

type LoadStatus = 'loading' | 'error' | 'ready';

function formatHours(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return '—';
  }
  return String(Number(value));
}

const UnavailableSection: React.FC<{ messageKey: string }> = ({ messageKey }) => {
  const { t } = useTranslation(NS);
  return (
    <p className="px-2 text-sm text-gray-500" role="status">
      {t(messageKey, { ns: NS })}
    </p>
  );
};

const HourBanksPanel: React.FC<{
  hourBanks: U2106HourBanks | null;
  hasOrganization: boolean;
  hasEmployee: boolean;
  hideVacationNegativeThresholdHint?: boolean;
}> = ({ hourBanks, hasOrganization, hasEmployee, hideVacationNegativeThresholdHint = false }) => {
  const { t } = useTranslation(NS);

  if (!hasOrganization) {
    return (
      <U2000Panel title={t('myProfile.hourBanks', { ns: NS })} titleAlign="left" className="w-full">
        <div className="mt-3">
          <UnavailableSection messageKey="myProfile.noOrganization" />
        </div>
      </U2000Panel>
    );
  }

  const rows: Array<{ label: string; initial: string; balance: string; hint?: string }> = hourBanks
    ? [
        {
          label: t('myProfile.vacation', { ns: NS }),
          initial: `${formatHours(hourBanks.vacationBncHoursInitial)} h`,
          balance: `${formatHours(hourBanks.vacationBncHours)} h`,
          hint: hideVacationNegativeThresholdHint
            ? undefined
            : `${t('myProfile.vacationNegativeThreshold', { ns: NS })} : ${formatHours(hourBanks.vacationNegativeThresholdHours)} h`,
        },
        {
          label: t('myProfile.holiday', { ns: NS }),
          initial: `${formatHours(hourBanks.holidayBncHoursInitial)} h`,
          balance: `${formatHours(hourBanks.holidayBncHours)} h`,
        },
        {
          label: t('myProfile.sickLeave', { ns: NS }),
          initial: `${formatHours(hourBanks.sickLeaveBncHoursInitial)} h`,
          balance: `${formatHours(hourBanks.sickLeaveBncHours)} h`,
        },
        {
          label: t('myProfile.special', { ns: NS }),
          initial: `${formatHours(hourBanks.specialBncHoursInitial)} h`,
          balance: `${formatHours(hourBanks.specialBncHours)} h`,
        },
      ]
    : [];

  return (
    <U2000Panel title={t('myProfile.hourBanks', { ns: NS })} titleAlign="left" className="w-full">
      <div className="mt-3 px-2">
        <p className="mb-3 text-xs text-gray-500">{t('myProfile.hourBanksHint', { ns: NS })}</p>
        {hourBanks && hasEmployee ? (
          <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between gap-4 bg-gray-50 px-3 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              <span>{t('myProfile.hourBankType', { ns: NS })}</span>
              <div className="flex shrink-0 gap-6">
                <span className="w-20 text-right">{t('myProfile.hourBankInitial', { ns: NS })}</span>
                <span className="w-20 text-right">{t('myProfile.hourBankBalance', { ns: NS })}</span>
              </div>
            </div>
            {rows.map((row) => (
              <div key={row.label} className="flex items-start justify-between gap-4 px-3 py-2.5">
                <dt className="text-sm text-gray-600">
                  {row.label}
                  {row.hint ? <div className="mt-0.5 text-xs text-gray-400">{row.hint}</div> : null}
                </dt>
                <dd className="flex shrink-0 gap-6 text-sm font-semibold tabular-nums text-gray-900">
                  <span className="w-20 text-right">{row.initial}</span>
                  <span className="w-20 text-right">{row.balance}</span>
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-gray-500">{t('myProfile.noHourBanks', { ns: NS })}</p>
        )}
      </div>
    </U2000Panel>
  );
};

const U2106ProfileForm: React.FC = () => {
  const { t } = useTranslation(NS);
  const { auth, hasRole } = useU2000AuthContext();
  const canEdit = hasRole(U2106ROLES.EDIT);
  const canMfa = hasRole(U2106ROLES.MFA);
  const axios = useU2000AxiosAuthenticated();
  const { loading, hourBanks, employeeMeta, loadProfile, saveProfile, revealSecretKey } = useU2106ProfileHandlers();
  const [loadStatus, setLoadStatus] = React.useState<LoadStatus>('loading');
  const [hideVacationNegativeThresholdHint, setHideVacationNegativeThresholdHint] = React.useState(false);
  const [hasEmployee, setHasEmployee] = React.useState(false);
  const [hasOrganization, setHasOrganization] = React.useState(false);
  const [mfaEnabled, setMfaEnabled] = React.useState(false);
  const [mfaEnrolled, setMfaEnrolled] = React.useState(false);
  const [ssoEnabled, setSsoEnabled] = React.useState(false);
  const [secretKeyValue, setSecretKeyValue] = useState('');
  const [secretKeyRevealed, setSecretKeyRevealed] = useState(false);
  const [secretKeyLoading, setSecretKeyLoading] = useState(false);
  /** Baseline JSON pour dirty fiables (SETDATA seul ne met pas à jour formState.old). */
  const baselineSnapshotRef = React.useRef<string | null>(null);
  const fieldsLocked = !canEdit || loading;
  const showEmployeeSections = hasEmployee;

  const showOrgHint =
    Array.isArray(auth.user.organizations) && auth.user.organizations.length > 1;
  const orgLabel = auth.user.organizationName?.trim() || null;

  const [formState, formStateDispatch] = useReducer(u2000FormState, {
    ...u2000IntFormState,
    state: U2000FormStateEnumState.EDIT,
    editMode: U2000FormStateEnumEditMode.EDIT,
  } as U2000FormState);

  const formStateHandlers = useU2000FormStateHandlers<U2106ProfileFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: {
      newRecord: () => emptyU2106ProfileFormRow(),
      validateForm: (row) =>
        u2106CollectProfileFormErrors(row, hasEmployee, {
          requiredField: t('myProfile.errors.requiredField', { ns: NS }),
          emailInvalid: t('myProfile.errors.emailInvalid', { ns: NS }),
        }),
    },
  });

  const applyProfileToForm = (result: U2106ProfileResult) => {
    setHasEmployee(result.hasEmployee === true);
    setHasOrganization(result.hasOrganization === true);
    setMfaEnabled(result.mfaEnabled === true);
    setMfaEnrolled(result.mfaEnrolled === true);
    setSsoEnabled(result.ssoEnabled === true);
    const row = u2106ProfileToFormRow(result);
    // afterSaveData pose current + old en une action ; setEditMode maintient l’édition
    // et reclône old. baselineSnapshotRef évite un dirty figé si formState.old reste null.
    formStateHandlers.afterSaveData(row);
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    baselineSnapshotRef.current = JSON.stringify(row);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadStatus('loading');
      const result = await loadProfile();
      if (cancelled) {
        return;
      }
      if (!result) {
        setLoadStatus('error');
        return;
      }
      applyProfileToForm(result);
      if (result.hasOrganization) {
        try {
          const initRes = await doU3129InitLU(axios);
          setHideVacationNegativeThresholdHint(
            u2000SkipHourBankBalanceValidationOnSubmit(initRes.data.result?.personalization),
          );
        } catch {
          setHideVacationNegativeThresholdHint(false);
        }
      } else {
        setHideVacationNegativeThresholdHint(false);
      }
      setLoadStatus('ready');
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- charge une fois au mount
  }, []);

  const currentRow = formStateHandlers.getData();
  const baseline = baselineSnapshotRef.current;
  const isDirty =
    baseline != null &&
    currentRow != null &&
    JSON.stringify(currentRow) !== baseline;

  const handleSubmit = async () => {
    if (!canEdit || loading || !isDirty) {
      return;
    }
    formStateHandlers.setSubmitted(true);
    const errors = formStateHandlers.getBusinessHandlers().validateForm?.(formStateHandlers.getData()) ?? {};
    formStateHandlers.setErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    const result = await saveProfile(formStateHandlers.getData());
    if (result) {
      applyProfileToForm(result);
      formStateHandlers.setSubmitted(false);
    } else {
      const refreshed = await loadProfile();
      if (refreshed) {
        applyProfileToForm(refreshed);
        formStateHandlers.setSubmitted(false);
      }
    }
  };

  const handleRevealSecretKey = async () => {
    if (secretKeyRevealed || secretKeyLoading) {
      return;
    }
    setSecretKeyLoading(true);
    try {
      const key = await revealSecretKey();
      if (key) {
        setSecretKeyValue(key);
        setSecretKeyRevealed(true);
      }
    } finally {
      setSecretKeyLoading(false);
    }
  };

  if (loadStatus === 'loading' && loading) {
    return (
      <div className="flex justify-center p-6 text-sm text-gray-500" aria-busy="true" role="status">
        {t('myProfile.loading', { ns: NS })}
      </div>
    );
  }

  if (loadStatus === 'error') {
    return (
      <div className="flex justify-center p-6">
        <div
          className="w-full max-w-lg rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
          role="alert"
        >
          {t('myProfile.loadError', { ns: NS })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full justify-center px-4 py-6">
      <form
        className="flex w-full flex-col gap-8 md:w-[75%]"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        noValidate
        aria-busy={loading}
      >
        {!hasOrganization ? (
          <p
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            role="status"
          >
            {t('myProfile.noOrganization', { ns: NS })}
          </p>
        ) : null}

        {showOrgHint && orgLabel && hasOrganization ? (
          <p className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900" role="note">
            {t('myProfile.orgContext', { ns: NS, org: orgLabel })}
          </p>
        ) : null}

        {/* Identité usager (sys_user) — prénom / nom + courriel compte ; clé secrète (BL-0402) */}
        <U2000Panel title={t('myProfile.identity', { ns: NS })} titleAlign="left" className="w-full">
          <div className="mt-3 space-y-3 px-2">
            <div className="flex w-full flex-wrap items-start gap-4">
              <div className="min-w-[120px] flex-1">
                <U2000InputText
                  fieldName="userFirstName"
                  label={t('myProfile.firstName', { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required
                  readonly={fieldsLocked}
                />
              </div>
              <div className="min-w-[120px] flex-1">
                <U2000InputText
                  fieldName="userLastName"
                  label={t('myProfile.lastName', { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required
                  readonly={fieldsLocked}
                />
              </div>
              <div className="min-w-[180px] flex-[1.5]">
                <U2000InputText
                  fieldName="userEmail"
                  label={t('myProfile.userEmail', { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  type="email"
                  required
                  readonly={fieldsLocked}
                />
              </div>
            </div>
            <div className="flex w-full flex-wrap items-end gap-4">
              <div className="min-w-[220px] flex-[2]">
                <div className="space-y-1.5">
                  <U2000Label label={t('myProfile.secretKey', { ns: NS })} required={false} />
                  <div className="flex flex-row items-center gap-4">
                    <input
                      type="text"
                      readOnly
                      disabled
                      value={secretKeyRevealed ? secretKeyValue : ''}
                      placeholder="••••••••"
                      className="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-muted px-4 py-3 text-sm text-gray-500"
                      aria-label={t('myProfile.secretKey', { ns: NS })}
                    />
                    {!secretKeyRevealed ? (
                      <button
                        type="button"
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-surface px-3 py-2 text-sm text-gray-700 hover:border-gray-400 hover:bg-muted disabled:opacity-50"
                        onClick={() => void handleRevealSecretKey()}
                        disabled={secretKeyLoading}
                        title={t('myProfile.revealSecretKey', { ns: NS })}
                        aria-label={t('myProfile.revealSecretKey', { ns: NS })}
                      >
                        <FaEye className="h-4 w-4" aria-hidden />
                        <span>{secretKeyLoading ? '…' : t('myProfile.revealSecretKey', { ns: NS })}</span>
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </U2000Panel>

        {canMfa && !ssoEnabled ? (
          <U2106MfaPanel initialEnabled={mfaEnabled} initialEnrolled={mfaEnrolled} />
        ) : null}

        {/* Employé — fiche RH indépendante de l’usager */}
        {showEmployeeSections ? (
          <U2000Panel title={t('myProfile.employee', { ns: NS })} titleAlign="left" className="w-full">
            <div className="mt-3 space-y-3 px-2">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
                <span>
                  {t('myProfile.codeEmployee', { ns: NS })}:{' '}
                  <span className="font-medium text-gray-900">{employeeMeta?.codeEmployee ?? '—'}</span>
                </span>
                <span>
                  {t('myProfile.numEmployee', { ns: NS })}:{' '}
                  <span className="font-medium text-gray-900">{employeeMeta?.numEmployee ?? '—'}</span>
                </span>
              </div>

              <div className="flex w-full flex-wrap items-start gap-4">
                <div className="min-w-[120px] flex-1">
                  <U2000InputText
                    fieldName="firstName"
                    label={t('myProfile.firstName', { ns: NS })}
                    formStateHandlers={formStateHandlers}
                    required
                    readonly={fieldsLocked}
                  />
                </div>
                <div className="min-w-[120px] flex-1">
                  <U2000InputText
                    fieldName="lastName"
                    label={t('myProfile.lastName', { ns: NS })}
                    formStateHandlers={formStateHandlers}
                    required
                    readonly={fieldsLocked}
                  />
                </div>
                <div className="w-38 shrink-0">
                  <U2000InputCalendarCustom
                    formStateHandlers={formStateHandlers}
                    fieldName="birthDate"
                    label={t('myProfile.birthDate', { ns: NS })}
                    dateFormat="yyyy-MM-dd"
                    required={false}
                    readonly={fieldsLocked}
                  />
                </div>
                <div className="flex shrink-0 flex-col space-y-1.5">
                  <div className="invisible pointer-events-none select-none" aria-hidden>
                    <U2000Label label={t('myProfile.birthDate', { ns: NS })} required={false} />
                  </div>
                  <div className="flex h-[34px] items-center">
                    <U2000CheckBox
                      formStateHandlers={formStateHandlers}
                      fieldName="hideBirthdayInCalendar"
                      label={t('myProfile.hideBirthday', { ns: NS })}
                      readonly={fieldsLocked}
                    />
                  </div>
                </div>
              </div>
            </div>
          </U2000Panel>
        ) : hasOrganization ? (
          <U2000Panel title={t('myProfile.employee', { ns: NS })} titleAlign="left" className="w-full">
            <div className="mt-3">
              <UnavailableSection messageKey="myProfile.noEmployee" />
            </div>
          </U2000Panel>
        ) : null}

        {/* Adresse — employé uniquement */}
        {showEmployeeSections ? (
          <U2000Panel title={t('myProfile.address', { ns: NS })} titleAlign="left" className="w-full">
            <div className="mt-3 flex w-full flex-wrap items-start gap-4 px-2">
              <div className="min-w-[160px] flex-2">
                <U2000InputText
                  fieldName="addressLine1"
                  label={t('myProfile.addressLine1', { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required
                  readonly={fieldsLocked}
                />
              </div>
              <div className="min-w-[120px] flex-1">
                <U2000InputText
                  fieldName="city"
                  label={t('myProfile.city', { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required
                  readonly={fieldsLocked}
                />
              </div>
              <div className="min-w-[100px] w-32">
                <U2000InputText
                  fieldName="postalCode"
                  label={t('myProfile.postalCode', { ns: NS })}
                  formStateHandlers={formStateHandlers}
                  required
                  readonly={fieldsLocked}
                />
              </div>
            </div>
          </U2000Panel>
        ) : hasOrganization ? (
          <U2000Panel title={t('myProfile.address', { ns: NS })} titleAlign="left" className="w-full">
            <div className="mt-3">
              <UnavailableSection messageKey="myProfile.noEmployee" />
            </div>
          </U2000Panel>
        ) : (
          <U2000Panel title={t('myProfile.address', { ns: NS })} titleAlign="left" className="w-full">
            <div className="mt-3">
              <UnavailableSection messageKey="myProfile.noOrganization" />
            </div>
          </U2000Panel>
        )}

        {/* Coordonnées — employé uniquement ; courriel personnel (pas userEmail) */}
        {showEmployeeSections ? (
          <U2000Panel title={t('myProfile.contact', { ns: NS })} titleAlign="left" className="w-full">
            <div className="mt-3 space-y-3 px-2">
              <div className="flex w-full flex-wrap items-start gap-4">
                <div className="min-w-[120px] flex-1">
                  <U2000InputMask
                    fieldName="phoneNumber"
                    label={t('myProfile.phoneNumber', { ns: NS })}
                    mask="phone"
                    formStateHandlers={formStateHandlers}
                    readonly={fieldsLocked}
                  />
                </div>
                <div className="min-w-[120px] flex-1">
                  <U2000InputMask
                    fieldName="cellPhone"
                    label={t('myProfile.cellPhone', { ns: NS })}
                    mask="phone"
                    formStateHandlers={formStateHandlers}
                    readonly={fieldsLocked}
                  />
                </div>
                <div className="min-w-[120px] flex-1">
                  <U2000InputText
                    fieldName="email"
                    label={t('myProfile.personalEmail', { ns: NS })}
                    formStateHandlers={formStateHandlers}
                    type="email"
                    readonly={fieldsLocked}
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('myProfile.personalEmailHint', { ns: NS })}</p>
                </div>
              </div>

              <div className="flex w-full flex-wrap items-start gap-4">
                <div className="min-w-[120px] flex-1">
                  <U2000InputMask
                    fieldName="otherPhone"
                    label={t('myProfile.otherPhone', { ns: NS })}
                    mask="phone"
                    formStateHandlers={formStateHandlers}
                    readonly={fieldsLocked}
                  />
                </div>
                <div className="w-24 shrink-0">
                  <U2000InputText
                    fieldName="otherPhoneExt"
                    label={t('myProfile.otherPhoneExt', { ns: NS })}
                    formStateHandlers={formStateHandlers}
                    readonly={fieldsLocked}
                  />
                </div>
                <div className="min-w-[140px] flex-2">
                  <U2000InputText
                    fieldName="otherPhoneComment"
                    label={t('myProfile.otherPhoneComment', { ns: NS })}
                    formStateHandlers={formStateHandlers}
                    readonly={fieldsLocked}
                  />
                </div>
              </div>
            </div>
          </U2000Panel>
        ) : hasOrganization ? (
          <U2000Panel title={t('myProfile.contact', { ns: NS })} titleAlign="left" className="w-full">
            <div className="mt-3">
              <UnavailableSection messageKey="myProfile.noEmployee" />
            </div>
          </U2000Panel>
        ) : (
          <U2000Panel title={t('myProfile.contact', { ns: NS })} titleAlign="left" className="w-full">
            <div className="mt-3">
              <UnavailableSection messageKey="myProfile.noOrganization" />
            </div>
          </U2000Panel>
        )}

        {/* Banques — consultation lecture seule (directives) */}
        <HourBanksPanel
          hourBanks={hourBanks}
          hasOrganization={hasOrganization}
          hasEmployee={hasEmployee}
          hideVacationNegativeThresholdHint={hideVacationNegativeThresholdHint}
        />

        {canEdit ? (
          <div className="flex justify-end border-t border-gray-100 pt-4">
            <U2000ActionBtn
              label={loading ? t('myProfile.submitting', { ns: NS }) : t('myProfile.save', { ns: NS })}
              icon={FaSave}
              handleClick={(e) => {
                e.preventDefault();
                void handleSubmit();
              }}
              disabled={loading || !isDirty}
            />
          </div>
        ) : null}
      </form>
    </div>
  );
};

export default U2106ProfileForm;
